import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface Page {
  id: string;
  title: string;
  slug: string;
  is_published: boolean;
  show_in_navigation: boolean;
  updated_at: string;
}

interface PagesResponse {
  pages: Page[];
}

export default function Pages() {
  const navigate = useNavigate();
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pageToDelete, setPageToDelete] = useState<Page | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<PagesResponse>('/admin/pages');
      setPages(response.pages);
    } catch (err) {
      setError('Failed to load pages. Please try again.');
      console.error('Error fetching pages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (page: Page) => {
    setPageToDelete(page);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!pageToDelete) return;

    try {
      setIsDeleting(true);
      await api.delete(`/admin/pages/${pageToDelete.id}`);
      setPages(pages.filter(p => p.id !== pageToDelete.id));
      setDeleteModalOpen(false);
      setPageToDelete(null);
    } catch (err) {
      console.error('Error deleting page:', err);
      alert('Failed to delete page. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setPageToDelete(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading pages...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4" role="alert">
        <Card padding="lg">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={fetchPages} variant="primary">
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pages</h1>
          <p className="text-gray-600 mt-1">Manage your CMS pages and content</p>
        </div>
        <Button
          variant="primary"
          size="md"
          onClick={() => navigate('/admin/pages/new')}
          aria-label="Add new page"
        >
          <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
          Add Page
        </Button>
      </div>

      <Card padding="none">
        {pages.length === 0 ? (
          <div className="text-center py-12 px-4">
            <p className="text-gray-500 mb-4">No pages found. Create your first page to get started.</p>
            <Button
              variant="primary"
              onClick={() => navigate('/admin/pages/new')}
            >
              <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
              Create Page
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" role="table" aria-label="Pages list">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Slug
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    In Navigation
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Updated
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pages.map((page) => (
                  <tr key={page.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <Link
                        to={`/admin/pages/${page.id}`}
                        className="text-blue-600 hover:text-blue-800 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                      >
                        {page.title}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      /{page.slug}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={page.is_published ? 'green' : 'gray'}>
                        {page.is_published ? 'Published' : 'Draft'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={page.show_in_navigation ? 'blue' : 'gray'}>
                        {page.show_in_navigation ? 'Yes' : 'No'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDate(page.updated_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/pages/${page.id}`)}
                          aria-label={`Edit ${page.title}`}
                        >
                          <Edit2 className="w-4 h-4" aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(page)}
                          aria-label={`Delete ${page.title}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" aria-hidden="true" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        title="Delete Page"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete <strong>{pageToDelete?.title}</strong>?
            This action cannot be undone.
          </p>
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleDeleteCancel}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              isLoading={isDeleting}
            >
              Delete Page
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
